// src/pages/Home.js

import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";

import MarketStats from "../components/MarketStats";

import {
  Wallet,
  ShieldCheck,
  Boxes,
  Zap,
  Gem,
  Github,
  MessageCircle,
  Send,
  Twitter,
} from "lucide-react";

import WalletBackground from "../components/WalletBackground";
import BaitLogo from "../BaitLogo.png";

import "./Home.css";

// =========================================================
// APIs
// =========================================================

const GOLD_API =
  "https://goldapi.bait-coin.com/api/gold-reference";

const GOLDCHAIN_RPC =
  "https://rpc.goldchain.tech";

// =========================================================
// CHAIN CONFIGURATION
// =========================================================

const GENESIS_SUPPLY = 100;
const BLOCK_REWARD = 2;
const CHAIN_ID = 777771;

// =========================================================
// RPC HELPER
// =========================================================

const rpcCall = async (method, params = []) => {
  const response = await fetch(GOLDCHAIN_RPC, {
    method: "POST",

    headers: {
      "Content-Type": "application/json",
    },

    body: JSON.stringify({
      jsonrpc: "2.0",
      id: Date.now(),
      method,
      params,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `RPC request failed: ${response.status}`
    );
  }

  const json = await response.json();

  if (json.error) {
    throw new Error(
      json.error.message || "RPC error"
    );
  }

  return json.result;
};

// =========================================================
// HEX TO NUMBER
// =========================================================

const hexToNumber = (hex) => {
  if (!hex) {
    return 0;
  }

  return parseInt(hex, 16);
};

// =========================================================
// FORMAT USD
// =========================================================

const formatUSD = (value) => {
  if (
    value === undefined ||
    value === null ||
    !Number.isFinite(Number(value))
  ) {
    return "$0.00";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

// =========================================================
// FORMAT NUMBER
// =========================================================

const formatNumber = (value) => {
  if (
    value === undefined ||
    value === null ||
    !Number.isFinite(Number(value))
  ) {
    return "0";
  }

  return new Intl.NumberFormat("en-US").format(
    Number(value)
  );
};

// =========================================================
// FORMAT PRICE
// =========================================================

const formatPrice = (value) => {
  if (
    value === undefined ||
    value === null ||
    !Number.isFinite(Number(value))
  ) {
    return "$0.00";
  }

  return `$${Number(value).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }
  )}`;
};

// =========================================================
// HOME COMPONENT
// =========================================================

function Home() {
  const navigate = useNavigate();

  // =======================================================
  // MARKET STATE
  // =======================================================

  const [marketData, setMarketData] = useState(null);

  const [blockNumber, setBlockNumber] = useState(0);

  const [circulatingSupply, setCirculatingSupply] =
    useState(0);

  // =======================================================
  // FETCH GOLD DATA
  // =======================================================

  const fetchGoldData = async () => {
    const response = await fetch(GOLD_API);

    if (!response.ok) {
      throw new Error(
        `Gold API request failed: ${response.status}`
      );
    }

    const data = await response.json();

    console.log("BAIT Gold API:", data);

    setMarketData(data);
  };

  // =======================================================
  // FETCH BLOCKCHAIN DATA
  // =======================================================

  const fetchBlockchainData = async () => {
    try {
      // ---------------------------------------------------
      // GET CHAIN ID
      // ---------------------------------------------------

      const chainIdHex =
        await rpcCall("eth_chainId");

      const chainId =
        hexToNumber(chainIdHex);

      console.log(
        "GoldChain ID:",
        chainId
      );

      // ---------------------------------------------------
      // VERIFY NETWORK
      // ---------------------------------------------------

      if (chainId !== CHAIN_ID) {
        throw new Error(
          `Wrong network. Expected ${CHAIN_ID}, received ${chainId}`
        );
      }

      // ---------------------------------------------------
      // GET CURRENT BLOCK
      // ---------------------------------------------------

      const blockHex =
        await rpcCall("eth_blockNumber");

      const currentBlock =
        hexToNumber(blockHex);

      console.log(
        "GoldChain block:",
        currentBlock
      );

      setBlockNumber(currentBlock);

      // ---------------------------------------------------
      // CALCULATE CIRCULATING SUPPLY
      // ---------------------------------------------------

      const estimatedSupply =
        GENESIS_SUPPLY +
        currentBlock * BLOCK_REWARD;

      setCirculatingSupply(
        estimatedSupply
      );

    } catch (err) {
      console.error(
        "GoldChain RPC error:",
        err
      );
    }
  };

  // =======================================================
  // LOAD MARKET DATA
  // =======================================================

  const fetchMarketData = async () => {
    try {
      await Promise.all([
        fetchGoldData(),
        fetchBlockchainData(),
      ]);
    } catch (err) {
      console.error(
        "Home market data error:",
        err
      );
    }
  };

  // =======================================================
  // INITIAL LOAD + AUTO REFRESH
  // =======================================================

  useEffect(() => {
    fetchMarketData();

    const interval = setInterval(
      fetchMarketData,
      60000
    );

    return () => {
      clearInterval(interval);
    };
  }, []);

  // =======================================================
  // ORIGINAL MARKET VALUES
  // =======================================================

  const baitPrice =
    Number(
      marketData?.bait?.price_usd
    ) || 0;

  const goldPerOz =
    Number(
      marketData?.gold?.usd_per_oz
    ) || 0;

  const goldPerGram =
    Number(
      marketData?.gold?.usd_per_gram
    ) || 0;

  const goldBacking =
    Number(
      marketData?.bait?.gold_backing_grams
    ) || 0;

  // =======================================================
  // ORIGINAL MARKET CAP LOGIC
  // =======================================================

  const marketCap =
    baitPrice * circulatingSupply;

  return (
    <div className="home-page">

      {/* =================================================
          ANIMATED BACKGROUND
      ================================================= */}

      <WalletBackground />

      {/* =================================================
          HERO
      ================================================= */}

      <section className="hero-section">

        {/* LOGO */}

        <div className="hero-logo-container">

          <img
            src={BaitLogo}
            alt="BAIT Logo"
            className="hero-logo"
          />

        </div>

       <h1 style={{  fontWeight: "1000" }}>
  BAIT PAY
</h1>

        <h2>
          Your Digital{" "}
          <span className="gold-text">
            GOLD
          </span>{" "}
          Wallet
        </h2>

        <div className="hero-buttons">

          <Button
            type="primary"
            size="large"
            className="wallet-btn"
            icon={<Wallet size={20} />}
            onClick={() =>
              navigate("/yourwallet")
            }
          >
            Create A Wallet
          </Button>

          <Button
            size="large"
            className="recover-btn"
            icon={<ShieldCheck size={20} />}
            onClick={() =>
              navigate("/recover")
            }
          >
            Sign In With Seed Phrase
          </Button>

        </div>

      </section>

      
      {/* =================================================
          LIVE MARKET STATISTICS
      ================================================= */}

      <MarketStats
        baitPrice={baitPrice}
        marketCap={marketCap}
        circulatingSupply={circulatingSupply}
        blockNumber={blockNumber}
        goldPerGram={goldPerGram}
        goldPerOz={goldPerOz}
        goldBacking={goldBacking}
        formatUSD={formatUSD}
        formatNumber={formatNumber}
        formatPrice={formatPrice}
      />

      {/* =================================================
          WEBSITE
      ================================================= */}

      <section className="website-section">
      </section>

      {/* =================================================
          SECURITY
      ================================================= */}

      <section className="security-banner">

        <div className="security-bottom">
        </div>

        {/* =================================================
            FOOTER
        ================================================= */}

        <footer className="wallet-footer">

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              maxWidth: "1000px",
              margin: "0 auto",
            }}
          >

            <span
              style={{
                color: "#8B6508",
              }}
            >
              Find Alt Coin Gems :
            </span>

            <a
              href="https://bait-coin.com"
              target="_blank"
              rel="noreferrer"
              style={{
                color: "#8B6508",
                textDecoration: "none",
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              www.Bait-coin.com
            </a>

          </div>

          <div
            style={{
              color: "#8B6508",
              fontSize: "0.75rem",
              textAlign: "center",
              marginTop: "1px",
              paddingTop: "10px",
            }}
          >
            © 2026 BAIT PAY Wallet.
            All rights reserved.
          </div>

        </footer>

      </section>

    </div>
  );
}

export default Home;